import { config } from './config';

/// Cognito hosted-UI sign-in over OAuth 2.0 authorization code + PKCE, the
/// same flow the native apps use. No AWS SDK: the hosted pages plus a redirect
/// do the whole job.
///
/// Token handling mirrors the apps as far as a browser allows. The id token is
/// held in memory only. The refresh token has to survive a reload to be worth
/// anything, so it goes in localStorage — which is readable by any script that
/// gets injected into this origin. That is the standard trade for a SPA, and
/// it is the reason the app ships no third-party scripts.

const REFRESH_KEY = 'steps.refreshToken';
const VERIFIER_KEY = 'steps.pkceVerifier';

let idToken: string | null = null;
let idTokenExpiry = 0;

function base64URL(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64URL(digest);
}

export function isSignedIn(): boolean {
  return localStorage.getItem(REFRESH_KEY) !== null;
}

/// Send the browser to the hosted sign-in page.
export async function beginSignIn(): Promise<void> {
  const verifier = base64URL(crypto.getRandomValues(new Uint8Array(32)).buffer);
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const url = new URL(`${config.authDomain}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', config.clientID);
  url.searchParams.set('redirect_uri', config.redirectURI);
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('code_challenge', await challengeFor(verifier));
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

export function signOut(): void {
  localStorage.removeItem(REFRESH_KEY);
  idToken = null;
  idTokenExpiry = 0;
}

class AuthError extends Error {}

async function exchange(parameters: Record<string, string>): Promise<void> {
  const response = await fetch(`${config.authDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(parameters).toString(),
  });

  if (!response.ok) {
    const body = await response.text();
    // A refresh token can be revoked, expire after 30 days, or belong to a
    // user that no longer exists. All of them come back as `invalid_grant`
    // and none are worth retrying: the only way out is a fresh sign-in.
    if (parameters.grant_type === 'refresh_token') {
      signOut();
      throw new AuthError('Your session has expired. Sign in again.');
    }
    throw new AuthError(describeTokenError(body));
  }

  const token = (await response.json()) as {
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  idToken = token.id_token;
  idTokenExpiry = Date.now() + token.expires_in * 1000;
  // Only the initial exchange returns a refresh token; a refresh does not.
  if (token.refresh_token) localStorage.setItem(REFRESH_KEY, token.refresh_token);
}

function describeTokenError(body: string): string {
  try {
    const failure = JSON.parse(body) as { error?: string; error_description?: string };
    switch (failure.error) {
      case 'invalid_grant':
        return 'That sign-in could not be completed. Try again.';
      case 'invalid_client':
        return 'This app is not configured for that sign-in server.';
      default:
        return failure.error_description ?? failure.error ?? body;
    }
  } catch {
    return body;
  }
}

/// Handle the `/callback` redirect. Returns true when a code was consumed.
export async function completeSignIn(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return false;

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!verifier) throw new AuthError('Sign-in did not start in this browser tab.');

  await exchange({
    grant_type: 'authorization_code',
    client_id: config.clientID,
    code,
    redirect_uri: config.redirectURI,
    code_verifier: verifier,
  });
  // Drop the code from the address bar so a reload cannot try to reuse it.
  window.history.replaceState({}, '', '/');
  return true;
}

/// A valid id token, refreshing when it is close to expiry. The API Gateway
/// authorizer validates the *id* token — its `aud` is the app client id, which
/// the access token would not match.
export async function validIDToken(): Promise<string> {
  if (idToken && idTokenExpiry > Date.now() + 60_000) return idToken;

  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) throw new AuthError('Not signed in.');

  await exchange({
    grant_type: 'refresh_token',
    client_id: config.clientID,
    refresh_token: refresh,
  });
  if (!idToken) throw new AuthError('Not signed in.');
  return idToken;
}

export function emailClaim(): string | null {
  if (!idToken) return null;
  // Read without verifying: it came straight from the token endpoint over
  // TLS and is only used to label the UI. Anything security-bearing is
  // checked server-side.
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as {
      email?: string;
    };
    return json.email ?? null;
  } catch {
    return null;
  }
}
